import * as React from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import {
  DayPicker,
  type DayPickerProps,
  getDefaultClassNames,
} from 'react-day-picker';
import { cn } from '@/lib/cn';

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  components,
  ...props
}: DayPickerProps) {
  const defaults = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        root: cn('w-fit', defaults.root),
        months: cn('relative flex flex-col', defaults.months),
        month: cn('flex flex-col gap-3', defaults.month),
        month_caption: cn(
          'relative mb-1 flex h-9 items-center justify-center',
          defaults.month_caption,
        ),
        caption_label: cn(
          'text-sm font-medium text-slate-900',
          defaults.caption_label,
        ),
        nav: cn(
          'absolute inset-x-0 top-0 flex items-center justify-between',
          defaults.nav,
        ),
        button_previous: cn(
          'inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-40',
          defaults.button_previous,
        ),
        button_next: cn(
          'inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-40',
          defaults.button_next,
        ),
        month_grid: cn('w-full border-collapse', defaults.month_grid),
        weekdays: cn('', defaults.weekdays),
        weekday: cn(
          'h-8 w-9 p-0 text-center text-[0.72rem] font-medium text-slate-500',
          defaults.weekday,
        ),
        week: cn('mt-1', defaults.week),
        day: cn('p-0 text-center', defaults.day),
        day_button: cn(
          'inline-flex h-9 w-9 items-center justify-center rounded-md p-0 text-sm font-normal text-slate-900 transition hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
          defaults.day_button,
        ),
        selected: cn(
          '[&>button]:bg-blue-600 [&>button]:font-medium [&>button]:text-white [&>button]:shadow-sm [&>button]:hover:bg-blue-600 [&>button]:focus-visible:ring-white/70',
          defaults.selected,
        ),
        outside: cn('text-slate-400', defaults.outside),
        disabled: cn('text-slate-300 opacity-40', defaults.disabled),
        hidden: cn('invisible', defaults.hidden),
        today: cn(
          // Selection styles live on `selected`; highlight “today” only when not selected.
          '[&:not([data-selected])>button]:bg-sky-100 [&:not([data-selected])>button]:font-semibold [&:not([data-selected])>button]:text-sky-900 [&:not([data-selected])>button]:ring-2 [&:not([data-selected])>button]:ring-inset [&:not([data-selected])>button]:ring-sky-400/70',
          defaults.today,
        ),
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: chClass }) => (
          <Icon
            icon={
              orientation === 'left' ? 'mdi:chevron-left' : 'mdi:chevron-right'
            }
            className={cn('size-5 text-slate-600', chClass)}
          />
        ),
        ...components,
      }}
      {...props}
    />
  );
}

export { Calendar };
